<script lang="ts" setup>
import { computed, ref, watch } from 'vue';
import { getLocalizedText } from '../utils/formHelpers';
import FieldRenderer from '../FieldRenderer.vue';
import RepeatGroupRenderer from '../RepeatGroupRenderer.vue';
import OTPRenderer from './OTPRenderer.vue';
import { shouldDisplayFormField, shouldDisplaySection } from '../types/displayRules';
import { useSectionDynamicFiltering } from '../hooks/useDynamicFiltering';
import { useFormStore } from '../store/useFormStore';
import { getOTPField } from '../types/formUtils';
import H4Title from '../../text/H4Title.vue';
import PContent from '../../text/PContent.vue';
import type { FormField, Section, ResponseInForm } from '../types/formTypeStructure';

interface ConditionalStats {
  sectionVisible: boolean;
  visibleFields: number;
  totalFields: number;
  hiddenFields: number;
  requiredVisible: number;
  requiredHidden: number;
  hiddenFieldNames: string[];
  conditionalFields: number;
  errors?: string[];
}

interface Props {
  section: Section;
  fields: FormField[];
  currentLang: string;
  fadeKey: number;
  formValues: Record<string, any>;
  debugInfo?: {
    sectionIndex: number;
    fieldPage: number;
    sectionCount: number;
    totalFieldPages: number;
    fieldsOnCurrentPage: number;
  };
  onFieldChange?: (fieldName: string, value: any) => void;
  onConditionalChange?: (stats: ConditionalStats) => void;
  onConditionalError?: (errors: string[]) => void;
  allSections?: Section[];
  onSubmit?: () => void;
  onNext?: () => void;
  isLastSection?: boolean;
  onClearForm?: () => void;
}

const props = defineProps<Props>();

// Log pour déboguer les changements de props
watch(() => props.fields, (newFields) => {
  console.log('📋 [FormSection] Champs reçus:', newFields?.map((f: FormField) => f.fieldName));
}, { immediate: true, deep: true });

watch(() => props.section, (newSection) => {
  console.log('📋 [FormSection] Section changée:', newSection?.sectionId, newSection?.title);
}, { immediate: true });

watch(() => props.fadeKey, (newKey) => {
  console.log('📋 [FormSection] fadeKey changé:', newKey);
}, { immediate: true });

// Auto-clean des valeurs obsolètes
const formStore = useFormStore();
watch([() => props.fadeKey, () => props.section.sectionId, () => props.fields], () => {
  // Nettoyer les valeurs obsolètes après le rendu
  const currentFields = props.fields.map(f => f.fieldName);
  const currentFormId = Object.keys(formStore.forms)[0];
  
  if (currentFormId) {
    const currentFormValues = formStore.getFormValues(currentFormId) || {};
    const valuesToKeep: Record<string, any> = {};
    
    Object.entries(currentFormValues).forEach(([key, value]) => {
      if (currentFields.includes(key)) {
        valuesToKeep[key] = value;
      }
    });
    
    // Nettoyer si des valeurs obsolètes existent
    const hasObsoleteValues = Object.keys(currentFormValues).some(key => !currentFields.includes(key));
    if (hasObsoleteValues) {
      formStore.setAllFormValues(currentFormId, valuesToKeep);
      console.log('🧹 [FormSection] Nettoyage des valeurs obsolètes');
    }
  }
}, { deep: true });

const allResponseInForms = computed<Record<string, ResponseInForm>>(() => {
  const result: Record<string, ResponseInForm> = {};
  for (const key in props.formValues) {
    result[key] = { responseValue: props.formValues[key] };
  }
  return result;
});

const shouldShowSection = computed(() =>
  shouldDisplaySection(props.section, allResponseInForms.value)
);

/**
 * Génère les classes CSS pour la largeur d'un champ selon sa propriété width
 * @param field Le champ pour lequel générer les classes
 * @returns Les classes CSS à appliquer
 */
function getFieldWidthClass(field: FormField): string {
  const width = field.width || "half"; // Défaut "half" si non spécifiée
  
  if (width === "full") {
    return "col-span-1 tablet:col-span-2 w-full"; // Full width sur tous les écrans
  } else {
    return "col-span-1 w-full"; // Half width (1 colonne mobile, 2 colonnes tablet+)
  }
}

// Appliquer le filtrage dynamique aux champs visibles
const formValuesRef = ref({ ...props.formValues });

// Fonction pour mettre à jour une valeur (utilisée par le hook de filtrage)
const setFieldValue = (fieldName: string, value: any, options?: { shouldValidate?: boolean }) => {
  formValuesRef.value[fieldName] = value;
  // Émettre l'événement pour mettre à jour le formulaire parent
  props.onFieldChange?.(fieldName, value);
};

// Appliquer le filtrage dynamique
const filteredFields = useSectionDynamicFiltering(
  props.fields,
  formValuesRef,
  setFieldValue
);

// Mettre à jour formValuesRef quand props.formValues change
watch(() => props.formValues, (newValues) => {
  formValuesRef.value = { ...newValues };
}, { deep: true, immediate: true });

// Filtrer les champs visibles après filtrage dynamique
const visibleFields = computed(() => {
  const visible = filteredFields.value.filter((f) => shouldDisplayFormField(f, allResponseInForms.value));
  console.log('👁️ [FormSection] Champs visibles:', visible.map((f: FormField) => f.fieldName), 'sur', filteredFields.value.length, 'champs filtrés');
  return visible;
});

const hiddenFields = computed(() =>
  props.fields.filter((f) => !shouldDisplayFormField(f, allResponseInForms.value))
);

const fieldStats = computed(() => {
  const requiredFields = props.fields.filter((f) =>
    f.validations?.some((v) => v.validationType === 'required')
  );
  const requiredVisible = visibleFields.value.filter((f) =>
    f.validations?.some((v) => v.validationType === 'required')
  );
  const requiredHidden = hiddenFields.value.filter((f) =>
    f.validations?.some((v) => v.validationType === 'required')
  );

  return {
    totalFields: props.fields.length,
    visibleFieldsCount: visibleFields.value.length,
    hiddenFieldsCount: hiddenFields.value.length,
    requiredTotal: requiredFields.length,
    requiredVisible: requiredVisible.length,
    requiredHidden: requiredHidden.length,
    hiddenFieldNames: hiddenFields.value.map((f) => f.fieldName),
    conditionalFields: props.fields.filter((f) => !!f.conditionalDisplay).length
  };
});

// Statistiques conditionnelles pour les callbacks
const conditionalStats = computed<ConditionalStats>(() => ({
  sectionVisible: shouldShowSection.value,
  visibleFields: fieldStats.value.visibleFieldsCount,
  totalFields: fieldStats.value.totalFields,
  hiddenFields: fieldStats.value.hiddenFieldsCount,
  requiredVisible: fieldStats.value.requiredVisible,
  requiredHidden: fieldStats.value.requiredHidden,
  hiddenFieldNames: fieldStats.value.hiddenFieldNames,
  conditionalFields: fieldStats.value.conditionalFields
}));

// Émettre les statistiques quand elles changent
watch(conditionalStats, (stats) => {
  props.onConditionalChange?.(stats);
}, { deep: true, immediate: true });

// Détecter si la section contient un champ OTP
const otpField = computed(() => {
  const field = getOTPField(props.section);
  if (field) {
    console.log("🔍 [FormSection] OTP Field détecté:", field.fieldName);
  }
  return field;
});
</script>

<template>
  <div v-if="!shouldShowSection && debugInfo" class="border-2 border-red-200 w-full max-w-4xl bg-red-50 rounded-lg p-4 opacity-50">
    <div class="text-red-600 text-sm">
      ⚠️ Section cachée par les conditions d'affichage
    </div>
    <div v-if="section?.title" class="text-xs text-red-500 mt-2">
      Section: {{ getLocalizedText(section.title, currentLang) || 'Sans titre' }}
    </div>
  </div>

  <div v-else-if="visibleFields.length === 0 && debugInfo" class="border-2 border-orange-200 w-full max-w-4xl bg-orange-50 rounded-lg p-4 opacity-50">
    <div class="text-orange-600 text-sm">
      <!-- ⚠️ Section visible mais tous les champs sont cachés ({{ fieldStats.totalFields }} champs) -->
    </div>
    <div class="text-xs text-orange-500 mt-2">
      Champs cachés: {{ fieldStats.hiddenFieldNames.join(', ') }}
    </div>
  </div>

  <!-- Si la section contient un OTP, afficher uniquement OTPRenderer -->
  <div
    v-else-if="otpField"
    :key="fadeKey"
    class="border-2 border-slate-200 w-full max-w-[1080px] bg-white rounded-[50px] p-2 tablet:p-8 flex flex-col items-center transition-opacity duration-500 opacity-100"
  >
    <!-- Header de la section -->
    <H4Title v-if="section.title" class="text-lg font-medium text-gray-700 mb-4 pb-2 border-b border-gray-200 w-full text-center">
      {{ getLocalizedText(section.title, currentLang) }}
    </H4Title>

    <PContent v-if="section.subTitle" class="text-gray-600 mb-4 w-full text-center">
      {{ getLocalizedText(section.subTitle, currentLang) }}
    </PContent>

    <!-- Afficher uniquement OTPRenderer -->
    <OTPRenderer
      :field="otpField"
      :form-values="formValues"
      :current-lang="currentLang"
      :all-sections="allSections || []"
      :on-submit="onSubmit"
      :on-next="onNext"
      :is-last-section="isLastSection || false"
      :on-clear-form="onClearForm || (() => {
        console.warn('onClearForm not provided, using fallback');
        const currentFormId = Object.keys(formStore.forms)[0];
        if (currentFormId) {
          formStore.clearForm(currentFormId);
        }
      })"
    />
  </div>

  <div
    v-else
    :key="fadeKey"
    class=" w-full max-w-[1080px]rounded-[50px] p-2 tablet:p-8 flex flex-col items-center transition-opacity duration-500 opacity-100"
  >
    <H4Title v-if="section.title" class="text-lg font-bold text-[#04406E]  w-full">
      {{ getLocalizedText(section.title, currentLang) }}
    </H4Title>

    <PContent v-if="section.subTitle" class="text-gray-600 mb-4 border-b border-gray-200 pb-4  w-full">
      {{ getLocalizedText(section.subTitle, currentLang) }}
    </PContent>

    <!-- Grille optimisée pour 4 champs : 2x2 sur grand écran, 1 colonne sur mobile -->
    <div class="grid grid-cols-1 tablet:grid-cols-2 gap-4 tablet:gap-6 w-full max-w-[1080px] [&_input:not([type='radio']):not([type='checkbox'])]:!w-full [&_textarea]:!w-full [&_select]:!w-full [&_*]:!max-w-full">
   
      <template v-for="field in visibleFields" :key="`${fadeKey}_${field.fieldName}`">
        <!-- Wrapper div pour appliquer les classes de largeur -->
        <div :class="getFieldWidthClass(field)">
          <RepeatGroupRenderer
            v-if="field.repeatGroup && field.repeatRule"
            :field="field"
            :formValues="formValues"
            :currentLang="currentLang"
            class="w-full"
          />
          <FieldRenderer
            v-else
            :field="field"
            :currentLang="currentLang"
            :formValues="formValues"
            class="w-full"
            @field-change="(fieldName, value) => props.onFieldChange?.(fieldName, value)"
          />
        </div>
      </template>
    </div>

    <!-- <div
      v-if="debugInfo"
      class="mt-4 text-xs text-gray-600 border-t pt-4 w-full bg-gray-50 rounded-md p-3"
    >
      <div class="flex flex-col gap-2">
        <p>Section {{ debugInfo.sectionIndex + 1 }}/{{ debugInfo.sectionCount }}</p>
        <p>Page {{ debugInfo.fieldPage + 1 }}/{{ debugInfo.totalFieldPages }} — Champs affichés : {{ visibleFields.length }}</p>
        <p class="text-green-600">
          Champs visibles : {{ visibleFields.length }}/{{ fields.length }}
        </p>
        <p v-if="visibleFields.length === 4" class="text-blue-600">
          ✓ Configuration optimale : 4 champs (2x2 sur tablet+, 1 colonne sur mobile)
        </p>
      </div>
    </div> -->
  </div>
</template>